/*
 * @format
 */

import * as types from '../action-types';

import config from '../../env';
import * as messages from '../web_socket_messages/messages';
import { GLOBAL_TIMEOUT } from '../utils/timeout';
import { isOwnPendingRequestId } from '../selectors/desired-state-selectors';

import { getPartnerAccessRequests } from './partner-access-requests-actions';
import { isDemoMode } from '../selectors';
import { clearDemoConfirmState } from './controls-actions';

const COMMAND_TIMEOUT = GLOBAL_TIMEOUT;
const timeout = ms => new Promise(resolve => setTimeout(resolve, ms));

export const logoutStarted = () => ({ type: types.LOGOUT_STARTED });
export const logoutCompleted = () => ({ type: types.LOGOUT_COMPLETED });
export const logoutFailed = () => ({ type: types.LOGOUT_FAILED });

export const signUpStarted = () => ({ type: types.SIGNUP_STARTED });
export const signUpComplete = () => ({ type: types.SIGNUP_COMPLETE });
export const signUpFailed = () => ({ type: types.SIGNUP_FAILED });

export const setAcceptTerms = (
  acceptTerms,
  acceptedTermsTime,
  termsVersion,
) => {
  if (!acceptTerms) {
    return {
      type: types.ACCEPT_TERMS_CLEARED,
    };
  }

  return {
    type: types.ACCEPT_TERMS_SET,
    acceptTerms,
    acceptedTermsTime,
    termsVersion,
  };
};

export const setAcceptMarketing = acceptMarketing => ({
  type: types.ACCEPT_MARKETING_SET,
  acceptMarketing,
});

export const darwinRegistrationRequired = () => ({
  type: types.DARWIN_REGISTRATION_REQUIRED,
});
export const darwinRegistrationStarted = () => ({
  type: types.DARWIN_REGISTRATION_STARTED,
});
export const darwinRegistrationFailed = () => ({
  type: types.DARWIN_REGISTRATION_FAILED,
});

export const darwinRegistrationCompleted = () => ({
  type: types.DARWIN_REGISTRATION_COMPLETED,
});

export const setFirstName = firstName => {
  return {
    type: types.DARWIN_FIRSTNAME_CHANGED,
    firstName,
  };
};

export const setLastName = lastName => {
  return {
    type: types.DARWIN_LASTNAME_CHANGED,
    lastName,
  };
};

export const registerDeviceAttemptsReset = () => ({
  type: types.DEVICE_REGISTRATION_ATTEMPTS_RESET,
});

export const listAllDevicesStarted = () => ({
  type: types.LIST_ALL_DEVICES_STARTED,
});
export const listAllDevicesSucceeded = gateways => ({
  type: types.LIST_ALL_DEVICES_SUCCEEDED,
  gateways,
});
export const listAllDevicesFailed = message => ({
  type: types.LIST_ALL_DEVICES_FAILED,
  message,
});

export const selectAHome = id => ({
  type: types.HOME_SELECTED,
  id,
});

export const selectAZone = zoneId => ({
  type: types.ZONE_SELECTED,
  zoneId,
});

export const haloNotificationMessagesInit = () => (dispatch, getState) => {
  // Demo mode has no backend for notification messages.
  if (isDemoMode(getState())) {
    return;
  }

  dispatch({ type: types.HALO_NOTIFICATION_MESSAGES_INIT });
};

export const homesMessageReceived = data => ({
  type: types.HOMES_MESSAGE_RECEIVED,
  data,
});

export const updateHomeProperty = (homeId, property) => ({
  type: types.UPDATE_HOME_PROPERTY,
  homeId,
  property,
});

export const pendingInvitesReceived = data => ({
  type: types.PENDING_INVITES_RECEIVED,
  data,
});

// A desiredState message's resolved[] carries this tick's terminal outcomes.
// For each outcome that names a request THIS client originated (clientRequestId
// recognised as ours — via the durable own-request set, which holds even after
// the local pending entry has gone, or a still-live pending entry), reconcile
// the local pending state:
//  - superseded ⇒ clear the local entry silently (the replacing goal arrived
//    in the same message's goals). This also clears any stale timeout error the
//    client-side command timeout raised while the request was still in flight
//    server-side, so a late supersession never strands an error;
//  - failed ⇒ no new action here (the local entry keeps today's per-type
//    behaviour and the slice goal removal arrives via last-writer-wins);
//  - success ⇒ handled by the existing completion / live-message machinery.
// Other clients' outcomes need no local action.
export const applyResolvedOutcomes = resolved => (dispatch, getState) => {
  if (!Array.isArray(resolved) || resolved.length === 0) {
    return;
  }

  const state = getState();

  resolved.forEach(outcome => {
    const clientRequestId = outcome && outcome.clientRequestId;

    if (!isOwnPendingRequestId(state, clientRequestId)) {
      return;
    }

    if (outcome.outcome === 'superseded') {
      dispatch({
        type: types.PENDING_CHANGE_SUPERSEDED,
        requestId: clientRequestId,
      });
    } else if (outcome.outcome === 'failed') {
      // A change THIS client made failed terminally. Drop the local optimistic
      // entry so the display reverts to reported (or whatever goal now stands)
      // rather than continuing to show the value that never took. Unlike
      // superseded, no replacing goal arrives; the failure itself is surfaced to
      // the user through the separate notification path.
      dispatch({
        type: types.PENDING_CHANGE_FAILED,
        requestId: clientRequestId,
      });
    }
  });
};

// this classifies each message and dispatches the appropriate action(s)
export const webSocketMessageReceived = data => dispatch => {
  const dispatchList = [];

  // mode changing messages
  switch (data.command) {
    case 'setPrtModeOff':
    case 'setPrtModeOn':
    case 'setPrtModeManual':
    case 'setPrtModeAuto':
    case 'setPrtModeOverrideNext':
    case 'setPrtModeOverridePlus':
    case 'cancelOverride':
    case 'cancelPrtHoliday':
      dispatchList.push({
        type:
          data.state === 'complete'
            ? types.WEBSOCKET_SET_PRT_MODE_RECEIVED
            : types.WEBSOCKET_SET_PRT_MODE_REJECTED,
        data,
      });
      break;

    default:
      break;
  }

  // setpoint changing messages
  switch (data.command) {
    case 'setPrtModeOff':
    case 'setPrtModeManual':
    case 'setPrtModeAuto':
    case 'setPrtModeOverrideNext':
    case 'setPrtModeOverridePlus':
    case 'cancelOverride':
    case 'cancelPrtHoliday':
      dispatchList.push({
        type:
          data.state === 'complete'
            ? types.WEBSOCKET_SET_SETPOINT_RECEIVED
            : types.WEBSOCKET_SET_SETPOINT_REJECTED,
        data,
      });

      break;

    default:
      break;
  }

  // other commands
  let actionName;
  switch (data.command) {
    case 'hello':
      actionName = types.WEBSOCKET_HELLO_MESSAGE_RECEIVED;
      break;

    case 'setPrtChSchedule':
    case 'setPrtDhwSchedule':
      actionName =
        data.state === 'complete'
          ? types.WEBSOCKET_SET_SCHEDULE_ACCEPTED
          : types.WEBSOCKET_SET_SCHEDULE_REJECTED;
      break;
    case 'setPrtHoliday':
    case 'cancelPrtHoliday':
      dispatchList.push({
        type:
          data.state === 'complete'
            ? types.WEBSOCKET_SET_HOLIDAY_MODE_RECEIVED
            : types.WEBSOCKET_SET_HOLIDAY_MODE_REJECTED,
        data,
      });
      break;
    case 'publish':
      actionName =
        data.state === 'complete'
          ? types.WEBSOCKET_PUBLISH_RECEIVED
          : types.WEBSOCKET_PUBLISH_REJECTED;
      break;

    case 'setPrtSettings':
    case 'setChildLock':
    case 'setIdentifyMode':
    case 'setZoneName':
    case 'unpairDevice':
    case 'hpiUnpairDevice':
    case 'timedDhw':
    case 'setPrtComfortTemperature':
    case 'setPrtReducedTemperature':
    case 'setDhwEmergencyOperation':
      actionName =
        data.state === 'complete'
          ? types.WEBSOCKET_PRT_SETTINGS_UPDATE_RECEIVED
          : types.WEBSOCKET_PRT_SETTINGS_UPDATE_REJECTED;
      break;

    case 'setHeatCurve':
    case 'setHeatCurveValue':
      actionName =
        data.state === 'complete'
          ? types.WEBSOCKET_INTERFACE_SETTINGS_UPDATE_RECEIVED
          : types.WEBSOCKET_INTERFACE_SETTINGS_UPDATE_REJECTED;
      break;

    case 'live':
      actionName = types.WEBSOCKET_LIVE_RECEIVED;
      break;
    case 'setPrtCustomProperties':
      actionName = types.CUSTOM_PROPERTIES_UPDATE_COMPLETED;
      break;
    case 'prtLive':
      actionName = types.WEBSOCKET_PRT_LIVE_RECEIVED;
      break;
    case 'notification':
      actionName = types.NOTIFICATION_RECEIVED;
      break;
    case 'fetchPendingDsrCommands':
      actionName = types.FETCH_PENDING_DSR_COMMANDS;
      break;
    case 'setDSREnable':
      actionName =
        data.state === 'complete'
          ? types.WEBSOCKET_SET_DSR_STATUS_RECEIVED
          : types.WEBSOCKET_SET_DSR_STATUS_REJECTED;
      break;

    case 'partnerGeneratedAccessRequestCreated':
      actionName = types.PARTNER_ACCESS_REQUESTS_CREATED;
      break;

    case 'haloNotificationMessageDelivered':
      actionName = types.HALO_NOTIFICATION_MESSAGES_FETCH;
      break;

    case 'desiredState':
      actionName = types.WEBSOCKET_DESIRED_STATE_RECEIVED;
      break;

    default:
      break;
  }

  // live telemetry
  switch (data?.action) {
    case 'halo-air-telemetry':
      actionName = types.WEBSOCKET_LIVE_DATA_RECEIVED;
      break;

    default:
      break;
  }

  // this the meta type for command progress
  if (data.type === 'commandStatus') {
    switch (data.status) {
      case 'ack':
        actionName = types.COMMAND_STATUS_ACK;
        break;
      case 'nak':
        actionName = types.COMMAND_STATUS_NACK;
        break;
    }
  }

  if (data.state === 'complete') {
    dispatchList.push({
      type: types.COMMAND_COMPLETED,
      message: data,
    });
  }

  if (actionName) {
    dispatchList.push({
      type: actionName,
      data,
    });
  }

  // Reconcile this client's own pending state against a desiredState push's
  // terminal outcomes BEFORE the slice actions run. The
  // WEBSOCKET_DESIRED_STATE_RECEIVED reducer drops every resolved requestId
  // from the durable own-request set as it ingests this push, so own-goal
  // recognition for these very outcomes has to read that set first — otherwise
  // a request that already completed or timed out locally is no longer
  // recognisable as ours and a trailing 'superseded' would strand its timeout
  // error. All updates in this synchronous pass are batched, so the render
  // still settles on the ingested goals.
  if (data.command === 'desiredState') {
    dispatch(applyResolvedOutcomes(data.resolved));
  }

  dispatchList.forEach(action => dispatch(action));
};

export const helloTimeout = () => ({
  type: types.WEBSOCKET_HELLO_MESSAGE_TIMEOUT,
});

export const clearHelloTimeout = () => ({
  type: types.WEBSOCKET_HELLO_MESSAGE_TIMEOUT_CLEARED,
});

export const refreshHelloMessage =
  (jsonApi, diagnosticsJsonApi) => async (dispatch, getState) => {
    // Demo mode has no backend - the fake property is already in the store.
    if (isDemoMode(getState())) {
      return;
    }

    dispatch(clearHelloTimeout());

    jsonApi.findAll('home').then(
      res => {
        dispatch(homesMessageReceived(res));
      },
      err => {
        console.log(err);
        dispatch(helloTimeout());
      },
    );

    dispatch(getPartnerAccessRequests(diagnosticsJsonApi));

    await timeout(COMMAND_TIMEOUT);
    dispatch(helloTimeout());
  };

export const CustomPropertiesStarted = () => ({
  type: types.CUSTOM_PROPERTIES_UPDATE_STARTED,
});

export const customPropertiesUpdateCompleted = () => ({
  type: types.CUSTOM_PROPERTIES_UPDATE_COMPLETED,
});

export const customPropertiesUpdateTimeout = () => ({
  type: types.CUSTOM_PROPERTIES_UPDATE_TIMEOUT,
});

export const customPropertiesUpdateReset = () => ({
  type: types.CUSTOM_PROPERTIES_UPDATE_RESET,
});

export const siRegistrationStarted = () => ({
  type: types.SI_REGISTRATION_STARTED,
});

export const siRegistrationCompletionStarted = () => ({
  type: types.SI_REGISTRATION_COMPLETION_STARTED,
});

export const siRegistrationCompletionTimeout = () => ({
  type: types.SI_REGISTRATION_COMPLETION_TIMEOUT,
});

export const siRegistrationReset = () => ({
  type: types.SI_REGISTRATION_RESET,
});

export const setZonesPending = homeId => ({
  type: types.SET_ZONES_PENDING,
  homeId,
});

export const clearZonesPending = () => ({
  type: types.CLEAR_ZONES_PENDING,
});

export const completeSiRegistration = sendWsMessage => async dispatch => {
  dispatch(siRegistrationCompletionStarted());
  sendWsMessage(messages.hello());
  await timeout(COMMAND_TIMEOUT);
  dispatch(siRegistrationCompletionTimeout());
};

export const dismissError = error => ({
  type: types.DISMISS_ERROR,
  requestId: error.requestId || error,
});

export const dismissPlusHours = () => ({
  type: types.PLUS_HOURS_DISMISSED,
});

export const ListAllDevices = Auth => async dispatch => {
  dispatch(listAllDevicesStarted());

  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const endpoint = `${config.halo_api_endpoint}/gateways`;

    const callResponse = await fetch(endpoint, {
      headers: {
        Authorization: token,
        Accept: 'application/json',
      },
      method: 'GET',
      type: 'gateways',
    });

    const body = await callResponse.json();

    if (callResponse.ok === true) {
      dispatch(listAllDevicesSucceeded(body));
    } else {
      console.log('Get gateways failed', body);
      dispatch(listAllDevicesFailed(body.errors[0].title));
    }
  } catch (e) {
    console.log('Get gateways failed', e);
    dispatch(listAllDevicesFailed('Exception'));
  }
};

// export const login = (email, password) => ({ type: types.LOGIN, email, password });

// Deletes this phone's push token, so a signed-out phone stops getting the
// account's notifications. The API needs the session to do it, so this runs
// before signing out (see useSignOut). Never throws: signing out goes ahead
// either way.
export const deletePushToken = jsonApi => async (dispatch, getState) => {
  const state = getState();
  const deviceToken = state.notifications?.deviceToken;

  // Demo mode has no Cognito session or push token.
  if (isDemoMode(state) || !jsonApi || !deviceToken) {
    return;
  }

  try {
    await jsonApi.destroy('push-token', deviceToken);
  } catch (error) {
    console.log('Delete device token failed', error);
  }
};

// Resets the store once the user has signed out. Signing out of Cognito is
// the auth context's job, not this one's.
export const logout = () => dispatch => {
  // Cancel any in-flight demo confirmation timers so they can't fire into
  // the next session (no-op outside demo mode).
  clearDemoConfirmState();
  dispatch(logoutCompleted());
};

export const updateDevicePushToken = deviceToken => ({
  type: types.NOTIFICATION_TOKEN_UPDATED,
  deviceToken,
});

export const addGeolocationDeviceToHome =
  (homeId, geolocationDevice) => async dispatch => {
    dispatch({
      type: types.GEOLOCATION_DEVICE_ADDED,
      data: {
        homeId,
        geolocationDevice: {
          id: geolocationDevice.GeolocationDeviceId,
          friendlyName: geolocationDevice.FriendlyName,
          deviceRegistrationId: geolocationDevice.DeviceRegistrationId,
          accountId: null,
          currentZone: null,
          previousZone: null,
          lastSeenAt: null,
          createdAt: Date.now(),
          updatedAt: null,
        },
      },
    });
  };

export const removeGeolocationDeviceFromHome =
  (homeId, geolocationDevice) => async dispatch => {
    dispatch({
      type: types.GEOLOCATION_DEVICE_REMOVED,
      data: { homeId, geolocationDevice },
    });
  };

export const updateGeolocationDevice =
  (homeId, geolocationDevice) => async dispatch => {
    dispatch({
      type: types.GEOLOCATION_DEVICE_UPDATED,
      data: { homeId, geolocationDevice },
    });
  };

export const pendingInviteAccepted = () => ({
  type: types.PENDING_INVITE_ACCEPTED,
});

export const dismissPendingInvite = pendingInvite => ({
  type: types.DISMISS_PENDING_INVITE,
  pendingInvite,
});

export const setLocationPending = () => ({
  type: types.GEOLOCATION_LOCATION_CONFIRMATION_PENDING,
});

export const setLocationFailed = () => ({
  type: types.GEOLOCATION_LOCATION_CONFIRMATION_FAILED,
});

export const setLocationCleared = () => ({
  type: types.GEOLOCATION_LOCATION_CONFIRMATION_CLEAR,
});

export const weatherOptimisationStarted = (homeId, requestId, enabled) => ({
  type: types.WEATHER_OPTIMISATION_STARTED,
  homeId,
  requestId,
  enabled,
});

export const weatherOptimisationTimeout = requestId => ({
  type: types.WEATHER_OPTIMISATION_TIMEOUT,
  requestId,
});

export const weatherOptimisationReset = requestId => ({
  type: types.WEATHER_OPTIMISATION_RESET,
  requestId,
});

export const setWeatherOptimisation =
  (sendWsMessage, homeId, enabled, requestId) => async dispatch => {
    dispatch(weatherOptimisationStarted(homeId, requestId, enabled));
    sendWsMessage(messages.setWeatherOptimisation(homeId, enabled, requestId));
    await timeout(COMMAND_TIMEOUT);
    dispatch(weatherOptimisationTimeout(requestId));
  };

export const setHolidayPending = () => ({
  type: types.SET_HOLIDAY_CONFIRMATION_PENDING,
});

export const setHolidayFailed = () => ({
  type: types.SET_HOLIDAY_CONFIRMATION_FAILED,
});

export const setHolidayCleared = () => ({
  type: types.SET_HOLIDAY_CONFIRMATION_CLEAR,
});

export const setHeatType = heatType => ({
  type: types.SET_HEAT_TYPE,
  heatType,
});

export const removeHome = homeId => ({
  type: types.REMOVE_HOME,
  homeId,
});

export const clearLiveData = homeId => ({
  type: types.CLEAR_LIVE_DATA,
  homeId,
});

export const disableDsr = (homeId, callback) => async dispatch => {
  dispatch({
    type: types.DISABLE_DSR,
    data: { homeId, callback },
  });
};

export const enableDsr = homeId => async dispatch => {
  dispatch({
    type: types.ENABLE_DSR,
    data: { homeId },
  });
};

export const getPendingDsrCommands = callback => ({
  type: types.FETCH_PENDING_DSR_COMMANDS,
  callback,
});

export const removePendingDsrCommand = id => ({
  type: types.REMOVE_PENDING_DSR_COMMANDS,
  pendingDsrCommand: { id },
});

export const resetPendingDsrCommandRequestStatus = () => ({
  type: types.RESET_PENDING_DSR_COMMANDS_REQUEST_STATUS,
});

export const updateDsrAutoAccept =
  (homeId, dsrAutoAccept) => async dispatch => {
    dispatch({
      type: types.DSR_AUTO_ACCEPT_UPDATE,
      data: { homeId, dsrAutoAccept },
    });
  };
